import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { ArrowRight, CheckCircle, Clock } from "lucide-react";
import { useScrollAnimation } from "../hooks/useScrollAnimation";

export default function Treatments() {
  const { t } = useTranslation();
  const [ref, isVisible] = useScrollAnimation();
  const [hairRef, hairVisible] = useScrollAnimation();

  const items = t("treatments.items", { returnObjects: true }) || [];
  const methodsData = t("treatments_page.hairTransplant.methodsData", { returnObjects: true }) || [];
  const pricingData = t("treatments_page.hairTransplant.pricingData", { returnObjects: true }) || [];
  const allIncludes = t("treatments_page.allIncludesItems", { returnObjects: true }) || [];

  return (
    <div className="pt-20">
      <Helmet>
        <title>Hair Transplants, Dental & Cosmetic Surgery in Turkey | Monimek</title>
        <meta name="description" content="Explore our premium medical treatments including FUE hair transplants, dental veneers, and cosmetic surgery at leading clinics in Istanbul." />
        <link rel="canonical" href="https://www.monizmek.com/treatments" />
      </Helmet>
      {/* Hero */}
      <section className="page-hero text-white">
        <div className="max-w-[1200px] mx-auto px-6 py-12 text-center">
          <span className="overline-text text-[#3FA7D6]">{t("treatments_page.hero.overline")}</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white font-outfit tracking-tight mt-3 mb-5" data-testid="treatments-hero-title">
            {t("treatments_page.hero.title")}
          </h1>
          <p className="text-white/75 text-lg max-w-2xl mx-auto">{t("treatments_page.hero.subtitle")}</p>
        </div>
      </section>

      {/* All Treatments Grid */}
      <section className="py-20 bg-[#F8FAFC]" ref={ref}>
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((item, i) => (
              <div
                key={i}
                className={`anim-init stagger-${(i % 3) + 1} ${isVisible ? "in-view" : ""} bg-white rounded-3xl overflow-hidden border border-gray-100 treatment-card group`}
                data-testid={`treatment-item-${i}`}
              >
                <div className={`h-3 w-full bg-gradient-to-r ${item.color}`} />
                <div className="p-8">
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="text-xl font-bold text-[#0B3C5D] font-outfit">{item.title}</h3>
                    <span className="text-xs font-semibold px-3 py-1 bg-[#3FA7D6]/12 text-[#3FA7D6] rounded-full ml-2 whitespace-nowrap">
                      {item.saving}
                    </span>
                  </div>
                  <p className="text-sm text-[#6B7280] leading-relaxed mb-6">{item.desc}</p>
                  <Link
                    to="/contact"
                    className="flex items-center gap-2 text-sm font-semibold text-[#0B3C5D] group-hover:text-[#3FA7D6] transition-colors"
                  >
                    {t("treatments_page.requestQuote")} <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Hair Transplant Spotlight */}
      <section className="py-20 bg-white" ref={hairRef}>
        <div className="max-w-[1200px] mx-auto px-6">
          <div className={`anim-init ${hairVisible ? "in-view" : ""}`}>
            <span className="overline-text">Featured Procedure</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0B3C5D] font-outfit tracking-tight mt-2 mb-4" data-testid="hair-transplant-section">
              {t("treatments_page.hairTransplant.title")}
            </h2>
            <p className="text-[#6B7280] leading-relaxed max-w-3xl mb-12">
              {t("treatments_page.hairTransplant.overview")}
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-10">
            {/* Methods Table */}
            <div className={`anim-init stagger-1 ${hairVisible ? "in-view" : ""}`}>
              <h3 className="text-xl font-bold text-[#0B3C5D] font-outfit mb-5">{t("treatments_page.hairTransplant.methods")}</h3>
              <div className="space-y-3">
                {methodsData.map((m, i) => (
                  <div key={i} className="bg-[#F8FAFC] rounded-2xl p-5 border border-gray-100" data-testid={`method-row-${i}`}>
                    <div className="flex justify-between items-start gap-4">
                      <div>
                        <div className="font-bold text-[#0B3C5D] text-sm mb-1">{m.method}</div>
                        <div className="text-xs text-[#6B7280]">{m.desc}</div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="flex items-center gap-1 text-xs text-[#6B7280] mb-1">
                          <Clock className="w-3 h-3" /> {m.duration}
                        </div>
                        <div className="text-xs text-[#3FA7D6] font-semibold">{m.advantage}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-[#6B7280] mt-4">{t("treatments_page.hairTransplant.recovery")}</p>
            </div>

            {/* Pricing */}
            <div className={`anim-init stagger-2 ${hairVisible ? "in-view" : ""}`}>
              <h3 className="text-xl font-bold text-[#0B3C5D] font-outfit mb-5">{t("treatments_page.hairTransplant.pricing")}</h3>
              <div className="bg-[#F8FAFC] rounded-3xl overflow-hidden border border-gray-100 mb-6">
                <div className="grid grid-cols-2 bg-[#0B3C5D] text-white p-4 text-sm font-semibold">
                  <div>Method</div>
                  <div>{t("treatments.costSavings")} (USD)</div>
                </div>
                {pricingData.map((p, i) => (
                  <div
                    key={i}
                    className={`grid grid-cols-2 p-4 text-sm border-t border-gray-100 ${i % 2 === 0 ? "bg-white" : "bg-[#F8FAFC]"}`}
                    data-testid={`pricing-row-${i}`}
                  >
                    <div className="font-semibold text-[#0B3C5D]">{p.method}</div>
                    <div className="text-[#3FA7D6] font-bold">{p.price}</div>
                  </div>
                ))}
              </div>

              {/* All Inclusive */}
              <div className="bg-[#0B3C5D] rounded-3xl p-6">
                <h4 className="font-bold text-white mb-4">{t("treatments_page.allInclusive")}</h4>
                <div className="space-y-2">
                  {allIncludes.map((inc, i) => (
                    <div key={i} className="flex items-center gap-2 text-white/80 text-sm">
                      <CheckCircle className="w-4 h-4 text-[#3FA7D6] flex-shrink-0" />
                      {inc}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-10 text-center">
            <Link to="/contact" data-testid="treatments-cta" className="btn-primary inline-flex">
              {t("treatments_page.requestQuote")} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
